/* Các tab trong hồ sơ khách tiềm năng: Lịch sử chạm, Nhắn tin, Nhu cầu (Lịch hẹn ở ApptTab) */
import React, { useState } from 'react';
import { Alert, AutoComplete, Button, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { CalendarClock, MessageCircle, PencilLine, PhoneIncoming, PhoneOutgoing, Shuffle, UserPlus } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { LYDO, TPL } from '../components/Data';
import { saveCustomerNeeds } from './api';
import { Field } from './BookingFields';
import { isDemoId } from './demo';
import {
  DICH_VUS, KENHS, MANGS, NEED_TAIL, codeLabel, dichVuOf, isConnected, mangOf, mmss, needFieldsOf, needValue, sourceOf, when,
  type DichVu, type LeadLocal, type Mang, type NeedField, type Needs,
} from './logic';
import { useLeadStore } from './store';

/* ---------------- Lịch sử chạm ---------------- */

const KIND_ICON = {
  call: PhoneOutgoing, msg: MessageCircle, note: PencilLine, stage: Shuffle, appt: CalendarClock,
} as const;

export function TouchTab({ r, l, now }: { r: ChannelCustomerItem; l: LeadLocal; now: number }) {
  const created = r.created_at ? dayjs(r.created_at).valueOf() : null;
  const src = sourceOf(r);
  // Ghi chú nhanh của Trực page (copy câu khách nhắn) ghim lên đầu để Telesales đọc trước khi gọi (họp 30/10)
  const pageNote = l.pageNote ?? (r.note?.trim() ? { text: r.note.trim(), by: r.in_charge_staff?.name ?? 'Trực page', at: created ?? 0 } : undefined);
  return (
    <>
    {pageNote && (
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
          <PencilLine size={12} />Ghi chú khách hàng · tạo bởi {pageNote.by}{pageNote.at ? ` · ${when(pageNote.at, now)}` : ''}
        </div>
        <div className="mt-1 whitespace-pre-line text-[13.5px] text-gray-800 dark:text-gray-100">{pageNote.text}</div>
      </div>
    )}
    <ol className="relative ml-2 space-y-3 border-l border-gray-200 pl-5 dark:border-gray-700">
      {l.touches.map((t) => {
        const Icon = t.kind === 'call' && t.dir === 'in' ? PhoneIncoming : KIND_ICON[t.kind];
        const ok = t.kind === 'call' && isConnected(t.code);
        return (
          <li key={t.id} className="relative">
            <span className={`absolute -left-[31px] top-0 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white dark:ring-gray-800 ${t.kind === 'call' ? (ok ? 'bg-emerald-500' : 'bg-gray-400') : 'bg-[#3e91ab]'}`}>
              <Icon size={11} className="text-white" />
            </span>
            <div className="text-[11px] text-gray-400">{when(t.at, now)} · {t.by}</div>
            <div className="text-[13px] text-gray-800 dark:text-gray-200">
              {t.kind === 'call' && <>{t.dir === 'in' ? 'Gọi đến' : 'Gọi đi'}{t.dur ? ` · ${mmss(t.dur)}` : ''} · <b>{t.code} {codeLabel(t.code)}</b></>}
              {t.kind !== 'call' && t.text}
            </div>
            {t.kind === 'call' && t.text && <div className="mt-0.5 text-[12px] italic text-gray-500">“{t.text}”</div>}
          </li>
        );
      })}
      {r.contact_staff?.name && (
        <li className="relative">
          <span className="absolute -left-[31px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-[#3e91ab] ring-4 ring-white dark:ring-gray-800"><Shuffle size={11} className="text-white" /></span>
          <div className="text-[11px] text-gray-400">Phân công</div>
          <div className="text-[13px] text-gray-800 dark:text-gray-200">Giao Telesales <b>{r.contact_staff.name}</b></div>
        </li>
      )}
      <li className="relative">
        <span className="absolute -left-[31px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-[#3e91ab] ring-4 ring-white dark:ring-gray-800"><UserPlus size={11} className="text-white" /></span>
        <div className="text-[11px] text-gray-400">{created ? when(created, now) : '—'}{r.in_charge_staff?.name ? ` · ${r.in_charge_staff.name}` : ''}</div>
        <div className="text-[13px] text-gray-800 dark:text-gray-200">Trực page tạo khách{src ? ` từ ${src}` : ''}</div>
      </li>
    </ol>
    </>
  );
}

/* ---------------- Nhắn tin ---------------- */

const MSG_TPL = TPL.filter(([id]) => id !== 'qr');

export function MsgTab({ r, l, xung, by, now }: { r: ChannelCustomerItem; l: LeadLocal; xung: string; by: string; now: number }) {
  const { addMsg, addTouch } = useLeadStore();
  const [tpl, setTpl] = useState<string>();
  const [draft, setDraft] = useState('');
  const appt = l.appts.find((a) => a.status !== 'cancelled' && a.at > now);
  const name = (r.display_name || r.fullname || '').trim();

  const fill = (id: string) => {
    const t = TPL.find((x) => x[0] === id)?.[2] ?? '';
    const X = xung.charAt(0).toUpperCase() + xung.slice(1);
    return t
      .replace(/\{xung\}/g, xung).replace(/\{Xung\}/g, X)
      .replace(/\{ten\}/g, name)
      .replace(/\{dv\}/g, appt?.service || (l.mang ? `dịch vụ ${l.mang}` : 'dịch vụ'))
      .replace(/\{gia\}/g, '[giá từ]')
      .replace(/\{han\}/g, dayjs(now).endOf('month').format('DD/MM/YYYY'))
      .replace(/\{ngay\}/g, appt ? dayjs(appt.at).format('HH:mm DD/MM') : '[ngày hẹn]')
      .replace(/Trần Hưng Đạo Q1/g, appt?.branch || 'Trần Hưng Đạo Q1');
  };

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    addMsg(r.id, { from: 'n', text, by });
    addTouch(r.id, { kind: 'msg', text: `Zalo · gửi tin${tpl ? ` “${MSG_TPL.find((x) => x[0] === tpl)?.[1]}”` : ''}`, by });
    setDraft('');
    setTpl(undefined);
    message.success('Đã gửi tin và ghi vào lịch sử chạm');
  };

  return (
    <div className="space-y-3">
      <Alert type="info" showIcon className="text-[12px]" message="Chưa nối Zalo OA — tin được lưu trên máy này để thử luồng, chưa gửi tới khách." />
      <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        {l.msgs.length === 0 && <div className="py-6 text-center text-[12px] text-gray-400">Chưa có tin nhắn với khách</div>}
        {l.msgs.map((m) => (
          <div key={m.id} className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] ${m.from === 'n' ? 'ml-auto bg-[#3e91ab] text-white' : 'border border-gray-200 bg-white text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
            <div className="whitespace-pre-wrap">{m.text}</div>
            <div className={`mt-1 text-[10.5px] ${m.from === 'n' ? 'text-white/70' : 'text-gray-400'}`}>{when(m.at, now)}{m.by ? ` · ${m.by}` : ''}</div>
          </div>
        ))}
      </div>
      <Select
        value={tpl}
        onChange={(v) => { setTpl(v); setDraft(v ? fill(v) : ''); }}
        allowClear
        placeholder="Chọn mẫu tin"
        className="w-full"
        options={MSG_TPL.map(([id, label]) => ({ value: id, label }))}
      />
      <Input.TextArea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Soạn tin; mẫu tự điền xưng hô, tên khách, ngày hẹn" maxLength={1000} showCount />
      <div className="flex items-center gap-3">
        <Button type="primary" disabled={!draft.trim()} onClick={send} className="!bg-[#3e91ab]">Gửi</Button>
        <span className="text-[11.5px] text-gray-500">Không hứa kết quả, không nêu giá chính thức trước khám.</span>
      </div>
    </div>
  );
}

/* ---------------- Nhu cầu ---------------- */

const opts = (xs: string[] = []) => xs.map((x) => ({ value: x, label: x }));
/** Trường nhiều ý lưu dạng "A · B" */
const splitMulti = (s?: string) => (s ? s.split(/\s*[·,]\s*/).filter(Boolean) : []);

function NeedInput({ f, n, set }: { f: NeedField; n: Needs; set: (p: Partial<Needs>) => void }) {
  const v = n[f.key];
  if (f.type === 'reason') {
    return (
      <div className="flex flex-col gap-1.5 sm:flex-row">
        <Select value={n.ly_do} onChange={(x) => set({ ly_do: x })} allowClear placeholder="Chọn lý do" options={opts(LYDO)} className="sm:!w-[46%]" />
        <Input value={n.ly_do_ct} onChange={(e) => set({ ly_do_ct: e.target.value })} placeholder="Chi tiết, VD: ốm dài, rụng nhiều" className="flex-1" />
      </div>
    );
  }
  if (f.type === 'select') return <Select value={v} onChange={(x) => set({ [f.key]: x })} allowClear placeholder="Chọn" options={opts(f.options)} />;
  if (f.type === 'multi') {
    return (
      <Select mode="tags" value={splitMulti(v)} onChange={(xs: string[]) => set({ [f.key]: xs.join(' · ') })} tokenSeparators={[',']} placeholder="Chọn hoặc gõ thêm" options={opts(f.options)} />
    );
  }
  // Ô của AutoComplete là input type=search: Esc bị trình duyệt xoá chữ vừa gõ → chặn lại, chỉ đóng gợi ý
  const keepOnEsc = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).blur();
  };
  return (
    <div onKeyDownCapture={keepOnEsc}>
      <AutoComplete
        value={v}
        onChange={(x) => set({ [f.key]: x })}
        options={opts(f.options)}
        filterOption={(input, o) => String(o?.value).toLowerCase().includes(input.toLowerCase())}
        placeholder={f.placeholder || (f.options?.length ? 'Chọn hoặc gõ' : '')}
        allowClear
        className="w-full"
      />
    </div>
  );
}

export function NeedsTab({ r, l, by }: { r: ChannelCustomerItem; l: LeadLocal; by: string }) {
  const { patch, addTouch } = useLeadStore();
  const [mang, setMang] = useState<Mang | undefined>(() => mangOf(r, l));
  const [kenh, setKenh] = useState(l.kenh);
  const [n, setN] = useState<Needs>(l.needs ?? {});
  const dv = dichVuOf(mang, n);
  const fields = needFieldsOf(mang, dv);
  const filled = needFieldsOf(mangOf(r, l), dichVuOf(mangOf(r, l), l.needs)).some((f) => needValue(f, l.needs ?? {}));
  // Đã có phiếu thì mở ở dạng xem như phiếu giấy, bấm Sửa để chỉnh
  const [edit, setEdit] = useState(!filled);
  const set = (p: Partial<Needs>) => setN((x) => ({ ...x, ...p }));
  const pickDv = (x: DichVu) => {
    if (x === dv) return;
    // Đổi dịch vụ thì bỏ câu trả lời riêng của dịch vụ cũ, giữ phần hỏi chung (lý do, rào cản, ngân sách, thời điểm)
    const own = fields.filter((f) => !NEED_TAIL.some((t) => t.key === f.key));
    set({ dich_vu: x, ...Object.fromEntries(own.map((f) => [f.key, undefined])) });
    if (!mang && x === 'Cấy tóc') setMang('Newhair');
  };
  const save = async () => {
    const needs = { ...n, dich_vu: dv };
    if (!isDemoId(r.id)) {
      const res = await saveCustomerNeeds(r.id, { mang, needs: { ...needs, ...(kenh ? { kenh } : {}) } });
      if (!res.ok) { message.error(res.message || 'Không lưu được nhu cầu khách'); return; }
    }
    patch(r.id, { mang, kenh, needs });
    addTouch(r.id, { kind: 'note', text: 'Cập nhật phiếu nhu cầu', by });
    message.success('Đã lưu nhu cầu khách');
    setEdit(false);
  };
  const cancel = () => { setMang(mangOf(r, l)); setKenh(l.kenh); setN(l.needs ?? {}); setEdit(false); };

  const title = (
    <div className="text-[12.5px] text-gray-500 dark:text-gray-400">
      {dv || mang
        ? <>Phiếu nhu cầu {dv ? 'dịch vụ' : 'mảng'} <b className="text-gray-800 dark:text-gray-100">{dv || mang}</b>{dv && mang ? ` · mảng ${mang}` : ''} — trường cố định</>
        : 'Chưa chọn dịch vụ — chọn dịch vụ để hiện đủ phiếu nhu cầu'}
    </div>
  );

  if (!edit) {
    const rows: [string, string][] = [
      ...fields.map((f): [string, string] => [f.label, needValue(f, n)]),
      ['Kênh khách muốn', kenh || ''],
      ...(n.ghi_chu?.trim() ? [['Ghi chú', n.ghi_chu.trim()] as [string, string]] : []),
    ];
    return (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          {title}
          <Button size="small" icon={<PencilLine size={13} />} onClick={() => setEdit(true)}>Sửa</Button>
        </div>
        <dl className="grid grid-cols-[minmax(110px,38%)_1fr] gap-x-3 gap-y-1.5 text-[13px] sm:grid-cols-[150px_1fr]">
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
              <dd className={`m-0 min-w-0 break-words ${v ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}>{v || '—'}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {title}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Mảng"><Select value={mang} onChange={setMang} allowClear placeholder="Chọn mảng" options={opts(MANGS)} /></Field>
        <Field label="Kênh khách muốn"><Select value={kenh} onChange={setKenh} allowClear placeholder="Chưa hỏi" options={opts(KENHS)} /></Field>
        <div className="col-span-full flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">Dịch vụ quan tâm</span>
          <div role="radiogroup" aria-label="Dịch vụ quan tâm" className="flex flex-wrap gap-1.5">
            {DICH_VUS.map((x) => (
              <button
                key={x}
                type="button"
                role="radio"
                aria-checked={dv === x}
                onClick={() => pickDv(x)}
                className={`rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors ${dv === x
                  ? 'border-[#3e91ab] bg-[#3e91ab] text-white'
                  : 'border-gray-300 text-gray-700 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-200'}`}
              >
                {x}
              </button>
            ))}
          </div>
        </div>
        {fields.map((f) => (
          <Field key={f.key} label={f.label} full={f.type === 'reason' || f.type === 'multi'}><NeedInput f={f} n={n} set={set} /></Field>
        ))}
        <Field label="Ghi chú thêm" full><Input.TextArea rows={2} value={n.ghi_chu} onChange={(e) => set({ ghi_chu: e.target.value })} /></Field>
      </div>
      <div className="flex gap-2">
        <Button type="primary" onClick={save} className="!bg-[#3e91ab]">Lưu nhu cầu</Button>
        {filled && <Button onClick={cancel}>Hủy</Button>}
      </div>
    </div>
  );
}
